<template><div><p class="d-docsite--paragraph">The ability to control an element's color is <em>only</em> provided as a class. We do not provide variables to control this. The text opacity class resets the font color's alpha channel CSS variable value.</p>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fco{n}</code> to change a font-color's opacity. You can also change font-color's opacity on <code v-pre>:hover</code>, <code v-pre>:focus</code>, <code v-pre>:focus-visible</code> by using the respective <code v-pre>h:d-fco{n}</code>, <code v-pre>f:d-fco{n}</code>, <code v-pre>fv:d-fco{n}</code> prefixes.</p>
<code-example source-code='&lt;p class=&quot;d-fc-critical&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco99&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco95&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco90&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco75&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco50&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco25&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco10&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco0&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;'>
<p class="d-fc-critical">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco99">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco95">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco90">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco75">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco50">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco25">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco10">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco0">The quick brown fox jumps over the lazy dog.</p>
</code-example>
<h3 id="inheritance-constraints" tabindex="-1" class="d-docsite--header-3">Inheritance Constraints <a class="header-anchor d-link d-docsite--link d-link" href="#inheritance-constraints" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Text opacity CSS Utilities won't be inherited by its children. A font-color utility class must be present on the same element to work.</p>
<div class="d-d-grid d-g-200 lg:d-g-cols2">
  <div>
    <dt-stack direction="row" gap="100" class="d-fc-warning d-mbe-100">
      <dt-icon name="alert-triangle" size="400" />
      <strong>Text opacity utility classes don't inherit colors.</strong>
    </dt-stack>
<code-example source-code='&lt;!-- This won&#39;t work --&gt;
&lt;div class=&quot;d-fc-critical&quot;&gt;
  &lt;p&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
  &lt;p class=&quot;d-fco50&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;/div&gt;'>
<!-- This won't work -->
<div class="d-fc-critical">
  <p>The quick brown fox jumps over the lazy dog.</p>
  <p class="d-fco50">The quick brown fox jumps over the lazy dog.</p>
</div>
</code-example>
  </div>
  <div>
<dt-stack direction="row" gap="100" class="d-fc-positive d-mbe-100">
  <dt-icon name="check-circle" size="400" />
  <strong>Apply each utility class to the same element</strong>
</dt-stack>
<code-example source-code='&lt;!-- This will --&gt;
&lt;div class=&quot;d-fc-critical&quot;&gt;
  &lt;p&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
  &lt;p class=&quot;d-fc-critical d-fco50&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;/div&gt;'>
<!-- This will -->
<div class="d-fc-critical">
  <p>The quick brown fox jumps over the lazy dog.</p>
  <p class="d-fc-critical d-fco50">The quick brown fox jumps over the lazy dog.</p>
</div>
</code-example>
  </div>
</div>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<utility-class-table>
  <template #content>
    <tbody>
      <tr v-for="i in opacity">
        <th scope="row" class="d-code--sm d-docsite-code">.d-fco{{ i }}</th>
        <td class="d-code--sm">--fco: {{ i }}% !important;</td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
</div></template>


<script setup>
  import { opacity } from '@data/type.json';
</script>